import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Calendar, Clock, MapPin } from 'lucide-react';
import DurationPill from '../../../components/booking/DurationPill';
import AvailabilityLegend from '../../../components/booking/AvailabilityLegend';
import { getFullDayName } from '../../../lib/utils';

export default function StepDateTime({
  // data
  locations, selectedLocation, selectedDate, selectedTimeSlot, duration,
  minDate, maxDate, timeSlots, slotPricing, availability, courtsPricing,
  slotAvailabilityStatuses, availabilityLoading,
  // derived
  breakInfo, operatingHoursDisplay, isLocationClosed,
  // helpers
  isTimeSlotPast, getConsecutiveSlots,
  // actions
  onLocationChange, onDateChange, onDurationChange,
  onTimeSlotClick, onNext,
}) {
  const selectedDayName = getFullDayName(selectedDate);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="step-datetime">
      <div>
        <h2 className="text-xl font-bold font-heading uppercase mb-4">Select Date & Time</h2>

        {/* Location */}
        <div className="mb-4">
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            <MapPin className="w-4 h-4 inline mr-1" />
            Location
          </Label>
          <Select value={selectedLocation?.id} onValueChange={onLocationChange}>
            <SelectTrigger data-testid="location-select" className="h-14 rounded-2xl">
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLocation && (
            <p className="text-xs text-muted-foreground mt-1">{selectedLocation.address}</p>
          )}
        </div>

        {/* Date */}
        <div className="mb-4">
          <Label htmlFor="date" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block" data-testid="date-label">
            <Calendar className="w-4 h-4 inline mr-1" />
            Date
            {selectedDayName && <span data-testid="date-day-of-week">: {selectedDayName}</span>}
          </Label>
          <Input
            id="date"
            data-testid="date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            min={minDate}
            max={maxDate}
            className="h-14 rounded-2xl"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Bookings available up to 1 month in advance
          </p>
        </div>

        {/* Operating hours */}
        {selectedLocation && selectedDate && (
          <Card className="p-4 mb-4 bg-muted/50">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Operating Hours:</span>
              <span className={`text-sm font-bold ${isLocationClosed ? 'text-red-600' : ''}`} style={!isLocationClosed ? { color: '#FF6B00' } : undefined}>
                {operatingHoursDisplay}
              </span>
            </div>
            {breakInfo && breakInfo.length > 0 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-bold text-amber-800 flex items-center gap-2">
                  🕌 Blocked Hours
                </p>
                {breakInfo.map((b) => (
                  <p key={`${b.start}-${b.end}`} className="text-sm text-amber-700 mt-1">
                    {b.start} - {b.end}: {b.reason || 'Break'}
                  </p>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Duration */}
        <div className="mb-4">
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-3 block">
            Session Duration
          </Label>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((hours) => (
              <button
                key={hours}
                data-testid={`duration-${hours}`}
                onClick={() => onDurationChange(hours)}
                className={`p-4 rounded-2xl border-2 transition-all font-bold ${
                  duration === hours
                    ? 'border-orange-500 bg-orange-500 text-white shadow-md'
                    : 'border-border hover:border-primary'
                }`}
              >
                <Clock className="w-4 h-4 mx-auto mb-1" />
                {hours} {hours === 1 ? 'Hour' : 'Hours'}
              </button>
            ))}
          </div>
        </div>

        {/* Time slot grid */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="uppercase text-xs font-bold text-muted-foreground block">
              {duration > 1 ? `Select Starting Time (${duration} consecutive hours)` : 'Time Slot'}
            </Label>
            {availabilityLoading && (
              <span className="text-xs text-muted-foreground" data-testid="availability-loading">
                Checking availability…
              </span>
            )}
          </div>

          {isLocationClosed ? (
            <Card className="p-6 text-center bg-red-50 border-red-200">
              <p className="text-red-600 font-bold">Location Closed</p>
              <p className="text-sm text-red-500 mt-1">
                {selectedLocation?.name} is closed on this day. Please select a different date.
              </p>
            </Card>
          ) : timeSlots.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-muted-foreground">No time slots available. Please select a location and date.</p>
            </Card>
          ) : (
            <>
              {duration > 1 && (
                <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-500 text-sm">
                  <p className="font-bold text-blue-800">ℹ️ Multi-Hour Booking</p>
                  <p className="text-blue-700 mt-1">
                    Select your starting time. Your booking will automatically include {duration} consecutive hours.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {timeSlots.map((slot) => {
                  const consecutive = duration > 1 ? getConsecutiveSlots(slot, duration) : [slot];
                  const isValidSelection = consecutive.length === duration;
                  const isSelected = selectedTimeSlot === slot;
                  const isHighlighted = duration > 1 && selectedTimeSlot
                    && getConsecutiveSlots(selectedTimeSlot, duration).includes(slot);
                  const isPast = isTimeSlotPast(slot);
                  const slotAvail = slotAvailabilityStatuses?.[slot];
                  const isFullyBooked = !!slotAvail?.is_fully_booked;
                  // status===null means the bulk fetch hasn't resolved yet
                  // (or a slot in the span failed to load) — treat as unknown/
                  // not-yet-bookable rather than silently falling through to
                  // "Available", which could let a customer pick an
                  // unverified slot.
                  const isUnknown = !isPast && slotAvail?.status == null;
                  const isDisabled = !isValidSelection || isPast || isFullyBooked || isUnknown;
                  const price = slotPricing[slot];

                  // Pre-compute slot styling so the JSX class block is a
                  // single expression instead of a deeply nested ternary.
                  // Base color mirrors the public "Check Availability" page's
                  // green/amber/red palette; Past/Selected/Included states
                  // are layered on top since they reflect the user's own
                  // action, not court supply.
                  let slotStyle = {};
                  let slotClass = 'min-h-[64px] rounded-[10px] transition-all text-left p-3 border-2';
                  if (isPast) {
                    slotClass += ' text-slate-400 cursor-not-allowed border-slate-200';
                    slotStyle = { background: '#F8FAFC', opacity: 0.5 };
                  } else if (isSelected) {
                    slotClass += ' text-white ring-2 ring-orange-300 border-transparent';
                    slotStyle = { background: '#FF6B00' };
                  } else if (isHighlighted) {
                    slotClass += ' text-orange-900 border-orange-300';
                    slotStyle = { background: '#FFF3E6' };
                  } else if (isFullyBooked) {
                    slotClass += ' bg-red-100 text-red-700 border-red-300 cursor-not-allowed';
                  } else if (!isValidSelection || isUnknown) {
                    slotClass += ' text-slate-400 cursor-not-allowed border-slate-200';
                    slotStyle = { background: '#F8FAFC', opacity: 0.5 };
                  } else if (slotAvail?.status === 'limited') {
                    slotClass += ' bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200';
                  } else {
                    slotClass += ' bg-green-100 text-green-700 border-green-300 hover:bg-green-200';
                  }

                  return (
                    <button
                      key={slot}
                      data-testid={`time-slot-${slot}`}
                      onClick={() => !isDisabled && onTimeSlotClick(slot)}
                      disabled={isDisabled}
                      className={slotClass}
                      style={slotStyle}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <Clock className="w-4 h-4 mb-1" />
                          <span className="font-bold">{slot}</span>
                        </div>
                        {price && !isPast && !isFullyBooked && (
                          <span className={`text-xs font-bold whitespace-nowrap ${isSelected ? 'text-white' : ''}`}>
                            BND {price.price_per_hour}/hr
                          </span>
                        )}
                      </div>
                      {isPast && <p className="text-xs mt-1">Passed</p>}
                      {!isPast && isFullyBooked && (
                        <p className="text-xs mt-1 font-bold" data-testid={`slot-fully-booked-${slot}`}>Fully Booked</p>
                      )}
                      {!isPast && isSelected && duration > 1 && (
                        <p className="text-xs mt-1 text-white font-bold">Start</p>
                      )}
                      {!isPast && isHighlighted && !isSelected && (
                        <p className="text-xs mt-1 text-orange-700">Included</p>
                      )}
                      {!isPast && !isSelected && !isHighlighted && !isFullyBooked && isValidSelection
                        && slotAvail?.available_courts != null && (
                        <p className="text-xs mt-1 font-semibold">
                          {slotAvail.available_courts}/{slotAvail.total_courts} left
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Availability + price summary */}
        {availability && selectedTimeSlot && (
          <Card className={`p-4 mt-4 ${availability.is_fully_booked ? 'bg-purple-50 border-purple-200' : 'glass-card'}`}>
            {duration > 1 && !availability.is_fully_booked && (
              <div className="mb-3 pb-3 border-b border-border/60">
                <DurationPill
                  duration={duration}
                  timeSlot={selectedTimeSlot}
                  variant="prominent"
                  testId="step-datetime"
                />
              </div>
            )}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-bold uppercase text-muted-foreground mb-1">
                  {availability.is_fully_booked ? 'Fully Booked' : 'Available Courts'}
                </p>
                {availability.is_fully_booked ? (
                  <p className="text-2xl font-black font-heading text-red-600">No courts left</p>
                ) : (
                  <p className="text-2xl font-black font-heading">
                    {availability.available_courts} / {availability.total_courts}
                  </p>
                )}
              </div>
              {courtsPricing[0] && (
                <div className="text-right">
                  <p className="text-sm font-bold uppercase text-muted-foreground mb-1">Est. Price</p>
                  <p className="text-2xl font-black font-heading text-primary">
                    BND {courtsPricing[0].price_per_hour}/hr
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        <AvailabilityLegend />
      </div>

      <Button
        onClick={onNext}
        data-testid="datetime-next-button"
        className="w-full h-14 btn-primary"
        disabled={!selectedDate || !selectedTimeSlot || isLocationClosed || availability?.is_fully_booked}
      >
        Next
        <ArrowRight className="ml-2 w-5 h-5" />
      </Button>
    </div>
  );
}
